import { useMemo, useState } from "react";
import DataEntryKpiSchoolsModal from "@/components/DataEntryKpiSchoolsModal";
import {
  AlertTriangle,
  Check,
  Repeat,
  ShieldCheck,
} from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import DataEntryGuidanceChip from "@/components/DataEntryGuidanceChip";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  buildCertificationDonutStyle,
  buildCriticalSchoolSubtitle,
  buildSchoolMonitoringActionQueue,
  buildSchoolMonitoringClosurePlan,
  buildSchoolMonitoringGaugeStyle,
  buildSchoolMonitoringInsightText,
  buildSchoolMonitoringNarrative,
  buildSchoolMonitoringPrimaryAction,
  computeSchoolMonitoringReadiness,
  fmt,
  fmtLakhs,
  fmtShortK,
  getRepeaterAction,
  getSchoolOpenCount,
  getSchoolRiskLabel,
  getSchoolStatusTag,
  pct,
  round1,
} from "@/utils/dataEntryDerived";

const InsightSimple = ({ icon: Icon, iconTone, title, value, text }) => (
  <article className="de-insight-card de-insight-simple">
    <div className={`de-insight-icon ${iconTone}`}>
      <Icon size={22} />
    </div>
    <h4>{title}</h4>
    <strong>{value}</strong>
    <p>{text}</p>
  </article>
);

const DataEntrySchoolMonitoringTab = ({
  overview,
  criticalSchools,
  criticalCount: criticalCountProp,
  highRepeaterSchools,
  certificationData,
  scopeLabel = "Pune District",
  guidance,
  guidanceProvider,
  guidanceLoading,
}) => {
  const [kpiMetricModal, setKpiMetricModal] = useState(null);
  const criticalCount = criticalCountProp ?? criticalSchools?.length ?? 0;
  const highRepeaterCount = highRepeaterSchools?.length ?? 0;
  const readiness = computeSchoolMonitoringReadiness(overview, criticalCount);
  const certRate = certificationData?.certification_rate ?? overview?.certification_rate ?? 0;
  const nonCertRate = round1(100 - certRate);
  const nonCertSchools =
    certificationData?.non_certified?.schools ?? overview?.non_certified_schools ?? 0;

  const localActionQueue = useMemo(
    () => buildSchoolMonitoringActionQueue(overview, criticalCount, highRepeaterCount),
    [overview, criticalCount, highRepeaterCount]
  );
  const localClosurePlan = useMemo(
    () => buildSchoolMonitoringClosurePlan(overview, criticalCount, highRepeaterCount),
    [overview, criticalCount, highRepeaterCount]
  );
  const actionQueue = guidance?.action_queue?.length ? guidance.action_queue : localActionQueue;
  const closurePlan = guidance?.closure_plan?.length ? guidance.closure_plan : localClosurePlan;
  const narrative =
    guidance?.narrative ?? buildSchoolMonitoringNarrative(overview, criticalCount, highRepeaterCount);
  const primaryAction =
    guidance?.primary_action ?? buildSchoolMonitoringPrimaryAction(overview, criticalCount, certRate);

  const insightText = (type) => {
    const fromGuidance = guidance?.insight_cards?.find((c) => c.type === type)?.text;
    return fromGuidance ?? buildSchoolMonitoringInsightText(type, overview, criticalCount, highRepeaterCount, certRate);
  };

  const scrollToActions = () => {
    document.getElementById("de-school-action-plan")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToTop = () => {
    document.querySelector(".de-dashboard")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openKpiModal = (metric) => () => setKpiMetricModal(metric);
  const onKpiKeyDown = (metric) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setKpiMetricModal(metric);
    }
  };

  return (
    <div className="de-school-tab" data-testid="data-entry-school-monitoring-tab">
      <section className="de-hero-card">
        <div className="de-hero-layout">
          <div>
            <div className="de-health-main">
              <div
                className="de-gauge"
                style={buildSchoolMonitoringGaugeStyle(readiness)}
                aria-label={`School monitoring readiness ${readiness} percent`}
              >
                <div>
                  <strong>{pct(readiness, 1)}</strong>
                  <span>Readiness</span>
                </div>
              </div>
              <div className="de-health-copy">
                <h2>School Monitoring Command Centre</h2>
                <p>{narrative}</p>
                <div className="de-status-row">
                  <DataEntryGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="red"
                    onClick={openKpiModal("critical_schools")}
                    ariaLabel={`${criticalCount} critical schools — view schools below 95 percent completion`}
                  >
                    {fmt(criticalCount)} Critical Schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="amber"
                    onClick={openKpiModal("high_repeaters")}
                    ariaLabel={`${highRepeaterCount} high repeater schools — view repeater risk schools`}
                  >
                    {fmt(highRepeaterCount)} High Repeater Schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="blue"
                    onClick={openKpiModal("certified_schools")}
                    ariaLabel="Certified schools — view officer-validated schools"
                  >
                    {pct(certRate, 1)} Certified
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="de-summary-note">
              <div className="de-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary officer action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="de-hero-metrics" aria-label="School monitoring KPIs">
            <article
              className="de-hero-metric danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Critical schools: view schools below 95 percent completion"
              onClick={openKpiModal("critical_schools")}
              onKeyDown={onKpiKeyDown("critical_schools")}
            >
              <small>Critical Schools</small>
              <strong>{fmt(criticalCount)}</strong>
              <span>&lt;95% completion</span>
            </article>
            <article
              className="de-hero-metric warning clickable"
              role="button"
              tabIndex={0}
              aria-label="High repeaters: view schools above 5 percent repeater rate"
              onClick={openKpiModal("high_repeaters")}
              onKeyDown={onKpiKeyDown("high_repeaters")}
            >
              <small>High Repeaters</small>
              <strong>{fmt(highRepeaterCount)}</strong>
              <span>&gt;5% repeater rate</span>
            </article>
            <article
              className="de-hero-metric green clickable"
              role="button"
              tabIndex={0}
              aria-label="Certified schools: view officer-validated schools"
              onClick={openKpiModal("certified_schools")}
              onKeyDown={onKpiKeyDown("certified_schools")}
            >
              <small>Certified Schools</small>
              <strong>{fmt(certificationData?.certified?.schools ?? overview?.certified_schools ?? 0)}</strong>
              <span>{pct(certRate, 1)} of schools</span>
            </article>
            <article
              className="de-hero-metric danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Non-certified: view schools awaiting certification"
              onClick={openKpiModal("non_certified")}
              onKeyDown={onKpiKeyDown("non_certified")}
            >
              <small>Non-certified</small>
              <strong>{fmt(nonCertSchools)}</strong>
              <span>Needs validation</span>
            </article>
            <article className="de-hero-metric">
              <small>Total Students</small>
              <strong>{fmtLakhs(overview?.total_students ?? 0)}</strong>
              <span>Monitored base</span>
            </article>
            <article
              className="de-hero-metric warning clickable"
              role="button"
              tabIndex={0}
              aria-label="Pending students: view schools with open records"
              onClick={openKpiModal("pending_students")}
              onKeyDown={onKpiKeyDown("pending_students")}
            >
              <small>Pending Students</small>
              <strong>{fmt(overview?.pending_students ?? 0)}</strong>
              <span>{pct(overview?.pending_rate ?? 0, 2)} pending</span>
            </article>
            <article
              className="de-hero-metric danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Repeaters: view schools with repeater records"
              onClick={openKpiModal("repeaters")}
              onKeyDown={onKpiKeyDown("repeaters")}
            >
              <small>Repeaters</small>
              <strong>{fmt(overview?.total_repeaters ?? 0)}</strong>
              <span>{pct(overview?.repeater_rate ?? 0, 2)} students</span>
            </article>
            <article className="de-hero-metric">
              <small>Total Schools</small>
              <strong>{fmt(overview?.total_schools ?? 0)}</strong>
              <span>{scopeLabel}</span>
            </article>
          </div>
        </div>
      </section>

      <div className="de-section-title">
        <div>
          <h2>Monitoring Intelligence</h2>
          <p>
            Convert long tables into quick-read exception buckets so officers can identify who needs immediate
            follow-up.
          </p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToActions}>
          View action plan →
        </button>
      </div>

      <section className="de-insight-grid">
        <InsightSimple
          icon={AlertTriangle}
          iconTone="red"
          title="Critical completion gap"
          value={fmt(criticalCount)}
          text={insightText("critical")}
        />
        <InsightSimple
          icon={Repeat}
          iconTone="amber"
          title="Repeater concentration"
          value={fmtShortK(overview?.total_repeaters ?? 0)}
          text={insightText("repeater")}
        />
        <InsightSimple
          icon={ShieldCheck}
          iconTone="red"
          title="Certification bottleneck"
          value={pct(nonCertRate, 1)}
          text={insightText("cert")}
        />
        <InsightSimple
          icon={Check}
          iconTone="green"
          title="Completion stability"
          value={pct(overview?.completion_rate ?? 0, 2)}
          text={insightText("completion")}
        />
      </section>

      <section className="de-monitoring-grid">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Critical School Queue</h3>
              <p>Highest priority schools below 95% completion, grouped for immediate field follow-up.</p>
            </div>
            <span className="de-pill red">Immediate action</span>
          </div>
          <div className="de-risk-list">
            {criticalSchools.slice(0, 5).map((school, idx) => {
              const risk = getSchoolRiskLabel(school.completion_pct ?? 0);
              const rankTone = risk.label === "High" ? "amber" : "";
              return (
                <div className="de-risk-item" key={school.udise_code}>
                  <span className={`de-queue-rank ${rankTone}`}>{idx + 1}</span>
                  <div>
                    <b>
                      <SchoolLink udiseCode={school.udise_code}>{school.school_name}</SchoolLink>
                    </b>
                    <span>{buildCriticalSchoolSubtitle(school)}</span>
                  </div>
                  <strong className={`de-risk-score ${risk.tone}`}>{risk.label}</strong>
                </div>
              );
            })}
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>School Certification Status</h3>
              <p>Certification is the key governance risk after entry completion.</p>
            </div>
            <span className="de-pill red">Backlog</span>
          </div>
          <div className="de-cert-grid">
            <div className="de-cert-donut" style={buildCertificationDonutStyle(certRate)}>
              <div>
                <strong>{pct(nonCertRate, 1)}</strong>
                <span>Not certified</span>
              </div>
            </div>
            <div className="de-cert-stack">
              <div className="de-cert-card green">
                <small>Certified Schools</small>
                <strong>{fmt(certificationData?.certified?.schools ?? 0)}</strong>
                <span>
                  {(certificationData?.certified?.students ?? 0).toLocaleString("en-IN")} students validated
                </span>
              </div>
              <div className="de-cert-card red">
                <small>Non-certified Schools</small>
                <strong>{fmt(certificationData?.non_certified?.schools ?? 0)}</strong>
                <span>
                  {(certificationData?.non_certified?.students ?? 0).toLocaleString("en-IN")} students pending
                  certification
                </span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="de-two-column">
        <article className="de-panel de-table-panel">
          <div className="de-table-head">
            <div>
              <h3>Critical Schools Below 95%</h3>
              <p>Sorted by lowest completion and largest unresolved status.</p>
            </div>
            <span className="de-pill red">Top 10</span>
          </div>
          <div className="de-table-wrap">
            <table className="de-table" aria-label="Critical schools below 95 percent completion">
              <thead>
                <tr>
                  <th>#</th>
                  <th>School Name</th>
                  <th>Block</th>
                  <th>Total</th>
                  <th>Completed</th>
                  <th>Open</th>
                  <th>Completion</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {criticalSchools.slice(0, 10).map((school, idx) => {
                  const open = getSchoolOpenCount(school);
                  const status = getSchoolStatusTag(school.completion_pct ?? 0);
                  const completionTone = (school.completion_pct ?? 0) < 90 ? "red" : "";
                  const openTone = open > 0 && (school.completion_pct ?? 0) < 90 ? "red" : "";
                  return (
                    <tr key={school.udise_code}>
                      <td>{idx + 1}</td>
                      <td>
                        <SchoolLink udiseCode={school.udise_code} className="de-school-link">
                          {school.school_name}
                        </SchoolLink>
                      </td>
                      <td>
                        <BlockLink blockName={school.block_name}>{school.block_name}</BlockLink>
                      </td>
                      <td>{fmt(school.total_students ?? 0)}</td>
                      <td>{fmt(school.total_completed ?? 0)}</td>
                      <td>
                        <span className={`de-tag ${openTone}`}>{fmt(open)}</span>
                      </td>
                      <td>
                        <span className={`de-tag ${completionTone}`}>{pct(school.completion_pct ?? 0, 2)}</span>
                      </td>
                      <td>
                        <span className={`de-tag ${status.tone}`}>{status.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>

        <article className="de-panel de-table-panel">
          <div className="de-table-head">
            <div>
              <h3>High Repeater Schools</h3>
              <p>Schools with high repeater concentration requiring audit review.</p>
            </div>
            <span className="de-pill amber">Data quality check</span>
          </div>
          <div className="de-table-wrap">
            <table className="de-table" aria-label="High repeater schools">
              <thead>
                <tr>
                  <th>#</th>
                  <th>School Name</th>
                  <th>Block</th>
                  <th>Students</th>
                  <th>Repeaters</th>
                  <th>Rate</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {highRepeaterSchools.slice(0, 10).map((school, idx) => {
                  const action = getRepeaterAction(school.repeater_rate ?? 0);
                  return (
                    <tr key={school.udise_code}>
                      <td>{idx + 1}</td>
                      <td>
                        <SchoolLink udiseCode={school.udise_code} className="de-school-link">
                          {school.school_name}
                        </SchoolLink>
                      </td>
                      <td>
                        <BlockLink blockName={school.block_name}>{school.block_name}</BlockLink>
                      </td>
                      <td>{fmt(school.total_students ?? 0)}</td>
                      <td>
                        <span className="de-tag red">{fmt(school.total_repeaters ?? 0)}</span>
                      </td>
                      <td>
                        <span className="de-tag">{pct(school.repeater_rate ?? 0, school.repeater_rate === 100 ? 0 : 2)}</span>
                      </td>
                      <td>
                        <span className={`de-tag ${action.tone}`}>{action.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <div className="de-section-title" id="de-school-action-plan">
        <div>
          <h2>Officer Action Queue and 30-Day Plan</h2>
          <p>Action cards convert exceptions into clear ownership, timeline, and closure logic.</p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToTop}>
          Back to top ↑
        </button>
      </div>

      <section className="de-school-action-grid">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Priority Action Queue</h3>
              <p>Recommended operational sequence for school monitoring closure.</p>
            </div>
            <span className="de-pill">Officer queue</span>
          </div>
          <div className="de-action-card">
            {actionQueue.map((item) => (
              <div className="de-school-action-item" key={item.title}>
                <div className={`de-icon-box ${item.tone}`}>
                  {item.tone === "red" ? (
                    <AlertTriangle size={22} />
                  ) : item.tone === "orange" ? (
                    <Repeat size={22} />
                  ) : (
                    <Check size={22} />
                  )}
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
                <span className={`de-tag ${item.tagTone}`}>{item.tag}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>30-Day School Monitoring Closure Plan</h3>
              <p>Simple governance rhythm for district and block review meetings.</p>
            </div>
            <span className="de-pill green">Closure plan</span>
          </div>
          <div className="de-closure-timeline">
            {closurePlan.map((step) => (
              <div className="de-timeline-item" key={step.week}>
                <b>{step.week}</b>
                <div>
                  <h4>{step.title}</h4>
                  <p>{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <DataEntryKpiSchoolsModal
        open={Boolean(kpiMetricModal)}
        metric={kpiMetricModal}
        onClose={() => setKpiMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiMetricModal === "pending_students"
            ? overview?.pending_rate
            : kpiMetricModal === "non_certified" || kpiMetricModal === "certified_schools"
              ? certRate
              : kpiMetricModal === "high_repeaters" || kpiMetricModal === "repeaters"
                ? overview?.repeater_rate
                : kpiMetricModal === "critical_schools"
                  ? overview?.completion_rate
                  : null
        }
        districtAggregate={
          kpiMetricModal === "pending_students"
            ? overview?.pending_students
            : kpiMetricModal === "repeaters"
              ? overview?.total_repeaters
              : null
        }
      />
    </div>
  );
};

export default DataEntrySchoolMonitoringTab;
